import { useEffect, useRef, useCallback } from "react";
import { ControllerProps, FieldValues } from "react-hook-form";
import debounce, { DebouncedFunc } from "lodash.debounce";
import { FormControlledMultiSelectDropdown } from "./FormControlledMultiSelectDropdown";
import { useHandleAllResidents } from "app/features/calendar/hooks/useHandleAllResidents";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";

export interface ResidentMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  helperText?: string;
  invalidMessage?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  onValueChange?: (value?: string[]) => void;
  locationId: number | undefined;
  preselectedResidents?: ResidentModel[];
}

export function FormControlledResidentMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  label = "Resident(s)",
  placeholder = "Choose resident(s)",
  helperText,
  invalidMessage,
  isDisabled,
  isReadOnly,
  isRequired,
  isInvalid,
  control,
  rules,
  onValueChange,
  locationId,
  preselectedResidents,
}: ResidentMultiSelectProps<TFieldValues>) {
  const { userOptions, areResidentsLoading, searchResidents } =
    useHandleAllResidents({
      selectedLocationId: locationId,
      preselectedResidents,
    });

  // Keep ref updated with latest searchResidents function
  const searchResidentsRef = useRef(searchResidents);

  useEffect(() => {
    searchResidentsRef.current = searchResidents;
  }, [searchResidents]);

  const debouncedSearch = useRef<DebouncedFunc<(searchText: string) => void>>(
    debounce((searchText: string) => {
      searchResidentsRef.current(searchText);
    }, 500),
  ).current;

  useEffect(() => {
    // Cancel the debounced call on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleResidentSearch = useCallback(
    (searchText: string) => {
      debouncedSearch(searchText);
    },
    [debouncedSearch],
  );

  return (
    <FormControlledMultiSelectDropdown<TFieldValues>
      name={name}
      label={label}
      placeholder={placeholder}
      helperText={helperText}
      invalidMessage={invalidMessage}
      options={userOptions}
      isDisabled={isDisabled || areResidentsLoading}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      isInvalid={isInvalid}
      control={control}
      rules={rules}
      onValueChange={onValueChange}
      onSearchChange={handleResidentSearch}
    />
  );
}
